import { useEffect, useMemo, useState } from "react";
import { DollarSign, AlertTriangle, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useListCarrierAddOnServices } from "@/api/carrier-add-on-services";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { useGetServiceAreas } from "@/api/service-areas";
import {
  useGetPickupRateBands,
  useAtomicSavePickupRateBands,
  type PickupRateBand,
  type AtomicSavePickupBandInput,
} from "@/api/pickup-rate-bands";
import { failureReason } from "@/lib/api-error";

/**
 * Pickup-rates editor — the Rates tab body of the pickup-service detail view.
 *
 * Always operates in locked-service mode: the caller passes a pickup service
 * id and the editor scopes the grid to (locked service → dispatch center →
 * validFrom). The grid is sender-service-area × weight bracket: one row per
 * sender area that already has bands for this (service, dispatch) scope, plus
 * an "Add service area" picker for any other area.
 *
 * Same band-key model as the workspace —
 * (fromServiceAreaId, dispatchCenterId, validFrom, carrierAddOnServiceId) —
 * and the same atomic-save payload shape. Every saved row names both ends of
 * the lane; if no explicit row exists, that pickup option is unavailable.
 */

// ---------------------------------------------------------------------------
// Cell + column model
// ---------------------------------------------------------------------------

interface BandColumn {
  weightFromKg: number;
  weightToKg: number;
}

interface BandCell {
  baseRateCents: number;
  perKgCents: number | null;
  isEmpty: boolean;
}

function emptyCell(): BandCell {
  return { baseRateCents: 0, perKgCents: null, isEmpty: true };
}

/** A grid row = one explicit sender service area. */
interface AreaRow {
  /** Stable key — "area:<id>". */
  key: string;
  fromServiceAreaId: string;
  label: string;
}

const formatCurrency = (cents: number | null): string =>
  cents == null ? "" : (cents / 100).toFixed(2);
const parseCurrency = (s: string): number | null | undefined => {
  if (s.trim() === "") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
};

/**
 * Input that holds a local draft and only commits the parsed value on blur —
 * lets the user type intermediate states like "4." without the cursor jumping.
 */
function CellDraftInput({
  value,
  onCommit,
  ...inputProps
}: {
  value: number | null;
  onCommit: (v: number | null) => void;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "onBlur"
>) {
  const [draft, setDraft] = useState(() => formatCurrency(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(formatCurrency(value));
  }, [value, focused]);

  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const parsed = parseCurrency(draft);
        if (parsed === undefined) {
          setDraft(formatCurrency(value));
        } else if (parsed !== value) {
          onCommit(parsed);
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Build grid state from server bands
// ---------------------------------------------------------------------------

interface GridState {
  columns: BandColumn[];
  /** areaKey → (column index → cell). */
  cells: Map<string, BandCell[]>;
  /** Ordered explicit sender-area rows. */
  areaRows: AreaRow[];
}

function buildGrid(
  bands: PickupRateBand[],
  dispatchCenterId: string,
  serviceAreas: { id: string; name: string }[]
): GridState {
  // Rate-band simplification: one row per scope+bracket. No validFrom/active
  // filtering. Only the exact dispatch belongs to this editor. A legacy
  // nullable row must never surface as a lane or influence the bracket grid.
  const slice = bands.filter(
    (b) => b.dispatchCenterId === dispatchCenterId && !!b.fromServiceAreaId
  );

  // Package billable-weight bands — union across every band in the slice.
  const colMap = new Map<string, BandColumn>();
  for (const b of slice) {
    const wf = Number(b.weightFromKg);
    const wt = Number(b.weightToKg);
    const k = `${wf}__${wt}`;
    if (!colMap.has(k)) colMap.set(k, { weightFromKg: wf, weightToKg: wt });
  }
  const columns = Array.from(colMap.values()).sort(
    (a, b) => a.weightFromKg - b.weightFromKg
  );

  // Area rows — every explicit sender area that has a band in the slice.
  const areaRows: AreaRow[] = [];
  const seenAreas = new Set<string>();
  for (const b of slice) {
    if (b.fromServiceAreaId == null) continue;
    if (seenAreas.has(b.fromServiceAreaId)) continue;
    seenAreas.add(b.fromServiceAreaId);
    const name =
      b.fromServiceArea?.name ??
      serviceAreas.find((a) => a.id === b.fromServiceAreaId)?.name ??
      b.fromServiceAreaId;
    areaRows.push({
      key: `area:${b.fromServiceAreaId}`,
      fromServiceAreaId: b.fromServiceAreaId,
      label: name,
    });
  }

  // Cells — keyed per area row, one per weight bracket.
  const cells = new Map<string, BandCell[]>();
  for (const ac of areaRows) {
    const areaBands = slice.filter(
      (b) => b.fromServiceAreaId === ac.fromServiceAreaId
    );
    cells.set(
      ac.key,
      columns.map((col) => {
        const m = areaBands.find(
          (b) =>
            Number(b.weightFromKg) === col.weightFromKg &&
            Number(b.weightToKg) === col.weightToKg
        );
        return m
          ? {
              baseRateCents: m.baseRateCents,
              perKgCents: m.perKgCents,
              isEmpty: false,
            }
          : emptyCell();
      })
    );
  }

  return { columns, cells, areaRows };
}

// ---------------------------------------------------------------------------
// Editor component
// ---------------------------------------------------------------------------

export function PickupRatesEditor({
  lockedServiceId,
}: {
  /** The pickup service this editor is scoped to (Rates tab of detail view). */
  lockedServiceId: string;
}) {
  const [dispatchCenterId, setDispatchCenterId] = useState<string>("");
  // Rate-band simplification: validFrom is a write-only timestamp on save —
  // not displayed, not used to scope reads. Stamped at save time below.
  const validFrom = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Resolve the locked service so we can scope dispatch centers to its
  // provider carrier. One unfiltered pickup-services query (the pattern the
  // access screen uses) — a CarrierAddOnService carries its own
  // providerCarrierId, so a single lookup gives us what we need.
  const { data: allPickupServices, isLoading: serviceLookupLoading } =
    useListCarrierAddOnServices({ type: "PICKUP" });
  const lockedService = useMemo(
    () => (allPickupServices ?? []).find((s) => s.id === lockedServiceId),
    [allPickupServices, lockedServiceId]
  );
  const serviceNotFound = !serviceLookupLoading && !lockedService;
  const providerCarrierId = lockedService?.providerCarrierId;

  // Dispatch centers, scoped to the locked service's provider carrier —
  // a DispatchCenter is keyed to its pickupProviderCarrierId.
  const { data: dispatchCenters } = useGetDispatchCenters();
  const dispatchList = useMemo(
    () =>
      (dispatchCenters ?? []).filter(
        (d) =>
          !!providerCarrierId && d.pickupProviderCarrierId === providerCarrierId
      ),
    [dispatchCenters, providerCarrierId]
  );

  const { data: serviceAreasResp } = useGetServiceAreas();
  const serviceAreas = useMemo(
    () => (serviceAreasResp ?? []).map((a) => ({ id: a.id, name: a.name })),
    [serviceAreasResp]
  );

  useEffect(() => {
    if (
      dispatchCenterId &&
      !dispatchList.some((d) => d.id === dispatchCenterId)
    ) {
      setDispatchCenterId("");
    }
  }, [dispatchList, dispatchCenterId]);

  // Auto-pick the first dispatch center once the scoped list loads.
  useEffect(() => {
    if (!dispatchCenterId && dispatchList.length > 0) {
      setDispatchCenterId(dispatchList[0].id);
    }
  }, [dispatchList, dispatchCenterId]);

  const scopeReady = !!dispatchCenterId;

  const dispatchPlaceholder = !providerCarrierId
    ? "Loading…"
    : dispatchList.length === 0
      ? "No dispatch centers for carrier"
      : "Select dispatch center";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Pickup rate scope</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="pr-dispatch">Dispatch center</Label>
            <Select
              value={dispatchCenterId}
              onValueChange={setDispatchCenterId}
              disabled={dispatchList.length === 0}
            >
              <SelectTrigger id="pr-dispatch" aria-label="Dispatch center">
                <SelectValue placeholder={dispatchPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {dispatchList.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {serviceLookupLoading ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : serviceNotFound ? (
        <div className="text-center p-8 text-muted-foreground">
          <AlertTriangle className="mx-auto h-12 w-12 mb-2 text-destructive opacity-70" />
          <p>Pickup service not found.</p>
        </div>
      ) : scopeReady ? (
        <PickupRateGrid
          carrierAddOnServiceId={lockedServiceId}
          dispatchCenterId={dispatchCenterId}
          dispatchCenterName={
            dispatchList.find((d) => d.id === dispatchCenterId)?.name ??
            "Dispatch center"
          }
          validFrom={validFrom}
          serviceAreas={serviceAreas}
        />
      ) : (
        <div className="text-center p-8 text-muted-foreground">
          <DollarSign className="mx-auto h-12 w-12 mb-2 opacity-50" />
          <p>Pick a dispatch center above to edit pickup rate bands.</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The editing grid — sender service area × weight bracket
// ---------------------------------------------------------------------------

function PickupRateGrid({
  carrierAddOnServiceId,
  dispatchCenterId,
  dispatchCenterName,
  validFrom,
  serviceAreas,
}: {
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  dispatchCenterName: string;
  validFrom: string;
  serviceAreas: { id: string; name: string }[];
}) {
  const {
    data: bands,
    isLoading,
    isError,
    refetch,
  } = useGetPickupRateBands({ carrierAddOnServiceId, dispatchCenterId });

  const saveMut = useAtomicSavePickupRateBands();

  // Local editable copy of the grid. Rebuilt whenever the server data or the
  // (dispatch, validFrom) scope changes. areaRows + columns are stored
  // side-by-side; iteration order (areas-as-rows × weight-brackets-as-cols) is
  // applied in the JSX below.
  const initial = useMemo<GridState>(() => {
    if (!bands) return { columns: [], cells: new Map(), areaRows: [] };
    return buildGrid(bands, dispatchCenterId, serviceAreas);
  }, [bands, dispatchCenterId, serviceAreas]);

  const [columns, setColumns] = useState<BandColumn[]>(initial.columns);
  const [areaRows, setAreaRows] = useState<AreaRow[]>(initial.areaRows);
  const [cells, setCells] = useState<Map<string, BandCell[]>>(initial.cells);

  useEffect(() => {
    setColumns(initial.columns);
    setAreaRows(initial.areaRows);
    setCells(initial.cells);
  }, [initial]);

  // Inline add-bracket inputs (now lives in the column-header row).
  const [bracketFrom, setBracketFrom] = useState("");
  const [bracketTo, setBracketTo] = useState("");
  const [bracketOpen, setBracketOpen] = useState(false);
  // Add-area popover state.
  const [areaPickerOpen, setAreaPickerOpen] = useState(false);

  function editCell(
    areaKey: string,
    colIdx: number,
    field: "baseRateCents" | "perKgCents",
    value: number | null
  ) {
    setCells((prev) => {
      const next = new Map(prev);
      const col = next.get(areaKey);
      if (!col) return prev;
      next.set(
        areaKey,
        col.map((c, i) =>
          i === colIdx ? { ...c, [field]: value, isEmpty: false } : c
        )
      );
      return next;
    });
  }

  function activateCell(areaKey: string, colIdx: number) {
    setCells((prev) => {
      const next = new Map(prev);
      const col = next.get(areaKey);
      if (!col) return prev;
      next.set(
        areaKey,
        col.map((c, i) => (i === colIdx ? { ...c, isEmpty: false } : c))
      );
      return next;
    });
  }

  function addBracket() {
    const wf = parseFloat(bracketFrom);
    const wt = parseFloat(bracketTo);
    if (!Number.isFinite(wf) || !Number.isFinite(wt) || wt <= wf) {
      toast.error("Enter a valid weight bracket (to > from).");
      return;
    }
    if (columns.some((c) => c.weightFromKg === wf && c.weightToKg === wt)) {
      toast.error("That weight bracket already exists.");
      return;
    }
    const nextCols = [...columns, { weightFromKg: wf, weightToKg: wt }].sort(
      (a, b) => a.weightFromKg - b.weightFromKg
    );
    const insertAt = nextCols.findIndex(
      (c) => c.weightFromKg === wf && c.weightToKg === wt
    );
    setColumns(nextCols);
    setCells((prev) => {
      const next = new Map<string, BandCell[]>();
      for (const [k, col] of prev) {
        const copy = [...col];
        copy.splice(insertAt, 0, emptyCell());
        next.set(k, copy);
      }
      return next;
    });
    setBracketFrom("");
    setBracketTo("");
    setBracketOpen(false);
  }

  function addAreaRow(areaId: string) {
    if (!areaId) return;
    const key = `area:${areaId}`;
    if (areaRows.some((a) => a.key === key)) {
      toast.error("That service area is already a row.");
      return;
    }
    const name = serviceAreas.find((a) => a.id === areaId)?.name ?? areaId;
    setAreaRows((prev) => [
      ...prev,
      { key, fromServiceAreaId: areaId, label: name },
    ]);
    setCells((prev) => {
      const next = new Map(prev);
      next.set(
        key,
        columns.map(() => emptyCell())
      );
      return next;
    });
    setAreaPickerOpen(false);
  }

  async function handleSave() {
    if (columns.length === 0) {
      toast.error("Add at least one weight bracket before saving.");
      return;
    }
    if (areaRows.length === 0) {
      toast.error("Add at least one sender service area before saving.");
      return;
    }

    try {
      const laneBands: AtomicSavePickupBandInput[] = [];
      for (const ac of areaRows) {
        const rowCells = cells.get(ac.key) ?? [];
        for (let i = 0; i < columns.length; i++) {
          const cell = rowCells[i];
          if (!cell || cell.isEmpty) continue;
          laneBands.push({
            fromServiceAreaId: ac.fromServiceAreaId,
            dispatchCenterId,
            weightFromKg: columns[i].weightFromKg,
            weightToKg: columns[i].weightToKg,
            baseRateCents: cell.baseRateCents,
            perKgCents: cell.perKgCents,
          });
        }
      }
      const fromServiceAreaIds = areaRows.map((a) => a.fromServiceAreaId);
      await saveMut.mutateAsync({
        carrierAddOnServiceId,
        validFrom,
        dispatchCenterId,
        fromServiceAreaIds,
        bands: laneBands,
        // The grid was hydrated from `bands`; narrow it to the slice this save
        // declares. The Server refuses rather than overwrite when it no longer
        // matches — otherwise a second editor's save reverts the first silently.
        basis: (bands ?? [])
          .filter((b) => fromServiceAreaIds.includes(b.fromServiceAreaId))
          .map((b) => ({ id: b.id, updatedAt: b.updatedAt })),
      });

      toast.success("Pickup rate bands saved.");
      void refetch();
    } catch (err) {
      // Relay the Server's own words — a stale-basis 409 explains itself, and
      // axios's "Request failed with status code 409" does not.
      toast.error(failureReason(err, "Failed to save pickup rates."));
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    // A failed bands fetch must NOT render as an empty grid — an admin could
    // re-enter rates and wipe what's already there.
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <AlertTriangle className="mx-auto h-10 w-10 mb-2 text-destructive opacity-70" />
          <p>Couldn&apos;t load pickup rate bands.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Service areas not yet a row — candidates for the add-area picker.
  const availableAreas = serviceAreas.filter(
    (a) => !areaRows.some((ac) => ac.fromServiceAreaId === a.id)
  );

  return (
    <div className="space-y-4">
      {/* Top bar — title + bracket controls + save. Brackets are workspace-wide
          so they live at the top, not inside any one lane card. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Pickup rate bands</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            One card per{" "}
            <strong>sender service area → {dispatchCenterName}</strong> lane.
            Missing lane pricing means no pickup option is offered.
          </p>
        </div>
        <Button onClick={() => void handleSave()} disabled={saveMut.isPending}>
          {saveMut.isPending ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save pickup rates
        </Button>
      </div>

      {/* Shared weight-bracket strip. Mirrors the rate-workspace pattern —
          brackets are a workspace-wide axis, lanes inherit them. */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">
            Package billable-weight bands
          </span>
          {columns.length === 0 ? (
            <span className="text-xs text-muted-foreground italic">
              none yet — add one to start editing rates
            </span>
          ) : (
            columns.map((col) => (
              <span
                key={`${col.weightFromKg}-${col.weightToKg}`}
                className="text-xs px-2 py-1 rounded bg-muted font-medium"
              >
                {col.weightFromKg} – {col.weightToKg} kg
              </span>
            ))
          )}
          <div className="ml-auto">
            <AddBracketPopover
              open={bracketOpen}
              onOpenChange={setBracketOpen}
              bracketFrom={bracketFrom}
              bracketTo={bracketTo}
              setBracketFrom={setBracketFrom}
              setBracketTo={setBracketTo}
              onAdd={addBracket}
            />
          </div>
        </CardContent>
      </Card>

      {/* One card per explicit lane. Directional title makes the sender →
          dispatch handoff obvious; matches the rate workspace's lane model. */}
      {areaRows.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No pickup lanes configured for {dispatchCenterName}. Add a sender
            service area below; no option is offered until its rates are set.
          </CardContent>
        </Card>
      )}
      {areaRows.map((ac) => (
        <Card key={ac.key}>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <span>{ac.label}</span>
              <span className="text-muted-foreground font-normal">→</span>
              <span>{dispatchCenterName}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {columns.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">
                Add a weight bracket above to start setting rates.
              </p>
            ) : (
              <div className="space-y-1.5">
                {columns.map((col, colIdx) => {
                  const cell = cells.get(ac.key)?.[colIdx] ?? emptyCell();
                  return (
                    <div
                      key={`${col.weightFromKg}-${col.weightToKg}`}
                      className="flex items-center gap-3 text-xs py-1"
                    >
                      <span className="text-muted-foreground w-28 shrink-0">
                        {col.weightFromKg} – {col.weightToKg} kg
                      </span>
                      {cell.isEmpty ? (
                        <button
                          type="button"
                          onClick={() => activateCell(ac.key, colIdx)}
                          className="flex items-center gap-1 px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          Add rate
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">$</span>
                          <CellDraftInput
                            type="number"
                            step="0.01"
                            value={cell.baseRateCents}
                            onCommit={(v) =>
                              editCell(ac.key, colIdx, "baseRateCents", v ?? 0)
                            }
                            aria-label={`Base per shipment ${ac.label} ${col.weightFromKg}-${col.weightToKg}kg`}
                            className="h-7 text-xs w-20 px-1.5"
                          />
                          <span className="text-muted-foreground">+</span>
                          <CellDraftInput
                            type="number"
                            step="0.01"
                            value={cell.perKgCents}
                            onCommit={(v) =>
                              editCell(ac.key, colIdx, "perKgCents", v)
                            }
                            placeholder="—"
                            aria-label={`Per-kg rate ${ac.label} ${col.weightFromKg}-${col.weightToKg}kg`}
                            className="h-7 text-xs w-16 px-1.5"
                          />
                          <span className="text-muted-foreground">/kg</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {availableAreas.length > 0 && (
        <Popover open={areaPickerOpen} onOpenChange={setAreaPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-full text-sm text-muted-foreground"
              aria-label="Add sender service area"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add sender service area
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-1">
            <div className="max-h-64 overflow-y-auto">
              {availableAreas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => addAreaRow(a.id)}
                  className="flex w-full items-center px-2 py-1.5 text-sm rounded hover:bg-muted"
                >
                  {a.name}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// Inline "+ bracket" affordance lives in the column-header row; isolating it
// keeps the giant grid JSX readable.
function AddBracketPopover({
  open,
  onOpenChange,
  bracketFrom,
  bracketTo,
  setBracketFrom,
  setBracketTo,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  bracketFrom: string;
  bracketTo: string;
  setBracketFrom: (v: string) => void;
  setBracketTo: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          aria-label="Add weight bracket"
        >
          <Plus className="h-3 w-3 mr-1" />
          Bracket
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3 space-y-2">
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="pr-bracket-from" className="text-xs">
              From (kg)
            </Label>
            <Input
              id="pr-bracket-from"
              type="number"
              step="0.1"
              value={bracketFrom}
              onChange={(e) => setBracketFrom(e.target.value)}
              className="h-8 w-24"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pr-bracket-to" className="text-xs">
              To (kg)
            </Label>
            <Input
              id="pr-bracket-to"
              type="number"
              step="0.1"
              value={bracketTo}
              onChange={(e) => setBracketTo(e.target.value)}
              className="h-8 w-24"
            />
          </div>
        </div>
        <Button size="sm" onClick={onAdd} className="w-full h-8">
          Add bracket
        </Button>
      </PopoverContent>
    </Popover>
  );
}
